import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';
import { errorText } from '../../utils/errorText';
import { badge, box } from '../shared/styles';
import Form from './Form';

/*
 * Non-conformances: anyone in the lab raises one; a quality manager moves it
 * open -> contained -> root_cause -> action -> verification -> closed, each
 * step with a reason. Corrective actions are done by their owner and verified
 * effective by someone else before the NC can close.
 */
const FLOW = ['open', 'contained', 'root_cause', 'action', 'verification', 'closed'];
const SEVERITY = { minor: 'info', major: 'warning', critical: 'danger' };
const CAPA_TONE = { open: 'info', done: 'warning', verified: 'success', ineffective: 'danger' };

function NcDetail({ id, onChanged, t }) {
  const { user } = useAuth();
  const [nc, setNc] = useState(null);
  const [people, setPeople] = useState([]);
  const [error, setError] = useState('');
  const manage = can(user, 'iso:manage');
  const load = useCallback(() => api.get(`/iso/nonconformances/${id}`).then(({ data }) => setNc(data))
    .catch((e) => setError(errorText(e))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (manage) api.get('/users/names').then(({ data }) => setPeople(data)).catch(() => setPeople([]));
  }, [manage]);
  const act = (path) => async (body) => { await api.post(path, body); await load(); onChanged(); };

  if (!nc) return error ? <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p> : <p>{t('loading')}</p>;
  const next = FLOW[FLOW.indexOf(nc.status) + 1];
  const moves = [next, nc.status === 'verification' ? 'action' : null].filter(Boolean);
  return (
    <div data-testid="nc-detail">
      <h3 style={{ marginTop: 0 }}>{nc.code} · {nc.title}
        <span style={badge(SEVERITY[nc.severity])}>{t(`opt_${nc.severity}`)}</span></h3>
      <ol style={{ display: 'flex', gap: 6, listStyle: 'none', padding: 0, flexWrap: 'wrap' }} data-testid="nc-flow">
        {FLOW.map((s) => (
          <li key={s} style={badge(s === nc.status ? 'primary' : FLOW.indexOf(s) < FLOW.indexOf(nc.status) ? 'success' : 'info')}>
            {t(`nc_${s}`)}</li>
        ))}
      </ol>
      <p>{nc.description}</p>
      <p style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t(`opt_${nc.source}`)} · {new Date(nc.opened_at).toLocaleString()}</p>
      {nc.containment && <p><strong>{t('containment')}:</strong> {nc.containment}</p>}
      {nc.root_cause && <p><strong>{t('root_cause')}:</strong> {nc.root_cause}</p>}

      <section style={box}>
        <h4 style={{ marginTop: 0 }}>{t('capa')}</h4>
        {nc.capa.length === 0 && <p style={{ fontSize: 13 }}>{t('noCapa')}</p>}
        {nc.capa.map((c) => {
          const mine = c.owner_id === user?.user_id;
          return (
            <div key={c.capa_id} style={{ borderTop: '1px solid var(--c-border)', padding: '6px 0' }} data-testid={`capa-${c.capa_id}`}>
              <div>{t(`opt_${c.kind}`)}: {c.description} · {c.owner_username} {c.due_on && `· ${t('dueOn')} ${c.due_on}`}
                <span style={badge(CAPA_TONE[c.status])} data-testid={`capa-status-${c.capa_id}`}>{t(`capa_${c.status}`)}</span></div>
              {c.done_note && <small>{t('doneNote')}: {c.done_note}</small>}
              {c.verification_note && <small> · {t('verificationNote')}: {c.verification_note}</small>}
              {['open', 'ineffective'].includes(c.status) && (mine || manage) && (
                <Form t={t} testId={`done-${c.capa_id}`} submitLabel="markDone" submitTip="tip.markDone" onSubmit={act(`/iso/capa/${c.capa_id}/done`)}
                  fields={[{ name: 'note', tip: 'tip.doneNote', required: true }, { name: 'reason', tip: 'tip.reason' }]} />
              )}
              {c.status === 'done' && manage && !mine && (
                <Form t={t} testId={`verify-${c.capa_id}`} submitLabel="verify" submitTip="tip.verifyCapa"
                  onSubmit={(v) => act(`/iso/capa/${c.capa_id}/verify`)({ ...v, effective: v.effective === 'yes' })}
                  fields={[{ name: 'effective', options: ['yes', 'no'], initial: 'yes', tip: 'tip.effective' },
                    { name: 'note', tip: 'tip.verifyNote', required: true }, { name: 'reason', tip: 'tip.reason' }]} />
              )}
              {c.status === 'done' && manage && mine && <small> · {t('notYourOwn')}</small>}
            </div>
          );
        })}
        {manage && ['contained', 'root_cause', 'action'].includes(nc.status) && (
          <Form t={t} testId="capa-form" submitLabel="addCapa" submitTip="tip.addCapa" onSubmit={act(`/iso/nonconformances/${nc.nc_id}/capa`)}
            fields={[{ name: 'kind', options: ['correction', 'corrective', 'preventive'], initial: 'corrective', tip: 'tip.capaKind' },
              { name: 'description', tip: 'tip.capaDescription' },
              { name: 'owner_id', label: 'owner', initial: '', tip: 'tip.owner',
                options: [{ value: '', label: '—' }, ...people.map((p) => ({ value: p.user_id, label: p.username }))] },
              { name: 'due_on', type: 'date', tip: 'tip.dueOn' }, { name: 'reason', tip: 'tip.reason' }]} />
        )}
      </section>

      {manage && moves.length > 0 && (
        <section style={box}>
          <h4 style={{ marginTop: 0 }}>{t('move')}</h4>
          <Form t={t} testId="transition-form" submitLabel="moveTo" submitTip="tip.moveTo"
            onSubmit={act(`/iso/nonconformances/${nc.nc_id}/transition`)}
            fields={[{ name: 'to', options: moves.map((m) => ({ value: m, label: t(`nc_${m}`) })), initial: moves[0], tip: 'tip.to' },
              ...(next === 'contained' ? [{ name: 'containment', type: 'textarea', tip: 'tip.containment' }] : []),
              ...(next === 'root_cause' ? [{ name: 'root_cause', type: 'textarea', tip: 'tip.rootCause' }] : []),
              { name: 'reason', tip: 'tip.reason' }]} />
        </section>
      )}

      {nc.history.length > 0 && (
        <section style={box}>
          <h4 style={{ marginTop: 0 }}>{t('history')}</h4>
          <ul style={{ fontSize: 13 }} data-testid="nc-history">{nc.history.map((h, i) => (
            <li key={i}>{new Date(h.at).toLocaleString()} · {t(`nc_${h.to}`)} · {h.by || t('system')}: {h.reason}</li>
          ))}</ul>
        </section>
      )}
    </div>
  );
}

export default function NonConformances() {
  const { t } = useTranslation('quality');
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => api.get('/iso/nonconformances').then(({ data }) => setList(data))
    .catch((e) => setError(errorText(e))), []);
  useEffect(() => { load(); }, [load]);
  const raise = async (v) => {
    const { data } = await api.post('/iso/nonconformances', v);
    await load();
    setSelected(data.nc_id);
  };
  return (
    <div>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {can(user, 'lab:observe') && (
        <section style={box}>
          <h4 style={{ marginTop: 0 }}>{t('raise')}</h4>
          <Form t={t} testId="raise-form" submitLabel="raise" submitTip="tip.raise" onSubmit={raise}
            fields={[{ name: 'title', tip: 'tip.ncTitle' }, { name: 'description', type: 'textarea', tip: 'tip.ncDescription' },
              { name: 'source', options: ['observation', 'internal_audit', 'complaint', 'experiment'], initial: 'observation', tip: 'tip.source' },
              { name: 'severity', options: ['minor', 'major', 'critical'], initial: 'minor', tip: 'tip.severity' }]} />
        </section>
      )}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <ul style={{ ...box, listStyle: 'none', minWidth: 240, flex: '0 1 320px', margin: 0 }}>
          {list.length === 0 && <li>{t('noNcs')}</li>}
          {list.map((n) => (
            <li key={n.nc_id}>
              <button type="button" data-testid={`nc-${n.code}`} title={t('tip.openNc')} onClick={() => setSelected(n.nc_id)}
                style={{ background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', textAlign: 'start',
                  color: 'var(--c-text-fg)', width: '100%' }}>
                <strong>{n.code}</strong> {n.title}
                <span style={badge(SEVERITY[n.severity])}>{t(`opt_${n.severity}`)}</span>
                <br /><small style={{ color: 'var(--c-text-muted-fg)' }}>{t(`nc_${n.status}`)}</small>
              </button>
            </li>
          ))}
        </ul>
        <div style={{ flex: '1 1 520px', minWidth: 0 }}>
          {selected ? <NcDetail key={selected} id={selected} onChanged={load} t={t} /> : <p>{t('chooseNc')}</p>}
        </div>
      </div>
    </div>
  );
}
